import { ApolloError } from '@apollo/client';
import useInterval from '@use-it/interval';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import {
  BookedUnit,
  GuestPassFragment,
  useclaimGuestPassMutation,
  usegetGuestPassQuery,
} from '../../apollo';
import { Button, Footer, Hero, LoadingOverlay, Modal, QRScanner } from '../../components';
import { ModalProps } from '../../components/Modal';
import TermsOfUseModal from '../../components/TermsOfUseModal';
import { getErrorMessage } from '../../helpers';
import { getPropertyEngrainMap } from '../../helpers/getPropertyEngrainMap';
import { useAcceptedTermsAt, useAnonymousUserId } from '../../hooks';
import { PropertyMapResponse } from '../../typing';
import { renderParagraph } from '../../utils';
import SGT from './SGT';
import styles from './styles';
interface VisitorConfig {
  header: string;
  subHeader?: string;
  pretext: string | null;
  timeText: string | null;
  buttonText?: string | null;
  expiresAt?: string | null;
  onClick?: (() => void | Promise<void>) | null;
}

interface GuestState {
  submitting: boolean;
  showQRScanner: boolean;
  guestPass: GuestPassFragment | null | undefined;
  isPending: boolean;
  isExpired: boolean;
}

interface PropertyAddress {
  street_name: string;
  unit_name: string;
  city_name: string;
  state_name: string;
  zip_code: string;
  country: string;
}

function getGuestPassState(guestPass?: GuestPassFragment | null) {
  if (!guestPass) {
    return { isPending: false, isExpired: false };
  }

  const { activatesAt, expiresAt } = guestPass;

  return {
    isPending: !!activatesAt && moment(new Date()).isBefore(activatesAt),
    isExpired: !!expiresAt && moment(new Date()).isSameOrAfter(expiresAt),
  };
}

function getFullAddress(address: PropertyAddress) {
  return `${address.street_name}, ${address.city_name}, ${address.state_name} ${address.zip_code}`;
}

const Visitor: React.FC = () => {
  const userRoleId = useParams().userRoleId as string;
  const anonymousUserId = useAnonymousUserId();
  const [state, setState] = useState<GuestState>({
    submitting: false,
    showQRScanner: false,
    guestPass: null,
    isPending: false,
    isExpired: false,
  });
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [acceptedTermsAt, setAcceptedTermsAt] = useAcceptedTermsAt();
  const [propertyMap, setPropertyMap] = useState<PropertyMapResponse | null>(null);
  const [selectedUnits, setSelectedUnits] = useState<BookedUnit[]>([]);
  const [searchParams] = useSearchParams();

  const { submitting, showQRScanner, guestPass } = state;
  const [modalState, setModalState] = useState<ModalProps>({
    show: false,
    title: '',
    message: '',
    buttons: [],
  });

  // Check every interval whether pass has expired
  useInterval(() => {
    setState(() => {
      return {
        ...state,
        ...getGuestPassState(state.guestPass),
      };
    });
  }, 1000);

  const showNetworkErrorModal = () => {
    setModalState({
      show: true,
      title: 'No internet connection',
      message: 'Please check your internet connection and try again',
    });
  };

  useEffect(() => {
    if (guestPass && guestPass.scopedProperty) {
      const property = guestPass.scopedProperty;
      const units = guestPass.appointmentInfo.bookedUnits;
      const propertyQueryParam = searchParams.get('property');
      const unitsQueryParam = searchParams.get('units');

      if (!propertyQueryParam && !unitsQueryParam && property.propertyMap) {
        setPropertyMap({
          name: property.name,
          address: property.address,
          externalAssetId: property.propertyMap.externalAssetId,
          externalMapId: property.propertyMap.externalMapId,
          type: property.propertyMap.type,
          externalUnits: property.propertyMap.externalUnits,
          externalFloors: property.propertyMap.externalFloors,
        });

        setSelectedUnits(units);
      }

      if (propertyQueryParam && unitsQueryParam) {
        const propertyMap = localStorage.getItem(propertyQueryParam);
        if (propertyMap) {
          setPropertyMap(JSON.parse(propertyMap));
        } else {
          getPropertyEngrainMap(propertyQueryParam).then((data) => {
            if (data.status === 0) {
              const propertyMap = {
                ...data.data,
                address: !!data.data.address ? getFullAddress(data.data.address) : null,
              };
              setPropertyMap(propertyMap);
              localStorage.setItem(propertyQueryParam, JSON.stringify(propertyMap));
            }
          });
        }

        // Right now pin code is the same for multiple locks/units
        const unitIds = unitsQueryParam.split('+');
        setSelectedUnits(unitIds.map(u => ({ unitId: u, pinCode: units[0]?.pinCode })));
      }
    }
  }, [guestPass, searchParams]);

  const { loading: getGuestPassLoading, error: getGuestPassError } = usegetGuestPassQuery({
    skip: !userRoleId,
    fetchPolicy: 'network-only',
    variables: { userRoleId: userRoleId as string },
    onCompleted: (guestPassData) => {
      const loadedGuestPass = guestPassData ? guestPassData.guestPass : null;

      setState({
        ...state,
        guestPass: loadedGuestPass,
        ...getGuestPassState(loadedGuestPass),
      });
    },
    onError: (err) => {
      if (err instanceof ApolloError && err.networkError) {
        showNetworkErrorModal();
      } else {
        setModalState({
          show: true,
          title: 'Guest pass unavailable',
          message: getErrorMessage(err, 'We were unable to load your guest pass.'),
        });
      }
    },
  });

  const [claimGuestPass] = useclaimGuestPassMutation({
    variables: { userRoleId: userRoleId as string },
  });

  if (!userRoleId || state.isExpired) {
    return (
      <Modal
        title="Guest pass unavailable"
        message={state.isExpired
          ? 'This guest pass has expired'
          : 'We were unable to load your guest pass.'
        }
        show
      />
    );
  }

  const onClickActivateHandler = async () => {
    try {
      setState({ ...state, submitting: true });

      const { data: claimGuestPassData } = await claimGuestPass();
      const claimedGuestPass = claimGuestPassData?.claimedGuestPass;

      setState({
        showQRScanner,
        guestPass: claimedGuestPass,
        submitting: false,
        ...getGuestPassState(claimedGuestPass),
      });
    } catch (err) {
      setModalState({
        show: true,
        title: 'Unable to claim guest pass',
        message: 'We were unable to claim your guest pass.',
        buttons: [],
      });

      setState({ ...state, submitting: false });
    }
  };

  if (getGuestPassLoading) {
    return <LoadingOverlay />;
  }

  if (getGuestPassError) {
    return <Modal
      show
      title={getGuestPassError.name === 'ApolloError' ? 'Guest pass unavailable' : getGuestPassError.name}
      message={`${getGuestPassError.message}.`}
    />;
  }

  if (guestPass && guestPass.scopedProperty) {
    const { scopedProperty, assignedToAnonymousUserId, activatesAt, expiresAt } = guestPass;
    const { image, name: propertyName } = scopedProperty;

    const cloudId = image ? image.cloudId : null;

    const isClaimed = anonymousUserId === assignedToAnonymousUserId;
    const canBeClaimed = !state.isPending && !isClaimed;

    const activationTime = activatesAt ? moment(activatesAt).format('MMM D, h:mma') : null;
    const expirationTime = expiresAt ? moment(expiresAt).format('MMM D, h:mma') : null;

    const getConfig = (): VisitorConfig | null => {
      if (!userRoleId || state.isExpired) {
        return {
          header: 'Guest Pass Expired!',
          subHeader: renderParagraph('This guest pass has expired and can no longer be used.'),
          pretext: 'This guest pass expired on',
          timeText: expirationTime,
        };
      }

      if (isClaimed) {
        return {
          header: 'Guest Pass Activated!',
          subHeader: renderParagraph('Scan the QR code posted on the Smart Signs to unlock entry points.'),
          pretext: 'You can access entry points until',
          timeText: expirationTime,
          buttonText: 'Scan QR Code',
          onClick: () => {
            setState({
              ...state,
              showQRScanner: true,
            });
          },
        };
      }

      if (canBeClaimed) {
        return {
          header: `Welcome to ${propertyName}`,
          subHeader: renderParagraph('Activate this Guest Pass to unlock common area doors on the property.'),
          pretext: null,
          timeText: null,
          buttonText: 'Activate Now',
          onClick: async () => {
            if (!acceptedTermsAt) {
              setIsTermsModalOpen(true);
            } else {
              onClickActivateHandler();
            }
          }
        }
      } else {
        if (state.isPending) {
          return {
            header: `Welcome to ${propertyName}`,
            subHeader: renderParagraph(`Guest pass cannot be activated until ${activationTime}.`),
            pretext: null,
            timeText: null,
            buttonText: 'Activate Now',
          };
        }


        return null;
      }
    };

    const config = getConfig();

    if (!config) return null;

    return (
      <>
        <Modal
          {...modalState}
          showAtBottom
          buttons={
            modalState.buttons || [{
              text: 'Dismiss',
              type: 'primary',
              size: 'large',
              onClick: () => {
                setModalState({
                  ...modalState,
                  show: false,
                });
              },
            }]
          }
        />

        {showQRScanner &&
          <QRScanner
            headerText={expirationTime || ''}
            headerSubtext="Access Expires"
            onClickBack={() => {
              setState({
                ...state,
                showQRScanner: false,
              });
            }}
          />
        }
        {((isClaimed && !propertyMap) || (!isClaimed)) &&
          <div className={styles.container}>
            <Hero
              status={'none'}
              title="Guest Pass"
              subtitle={propertyName}
              cloudId={cloudId}
              imageAlt={propertyName}
            />
            <div className={styles.header}>
              {config.header}
            </div>
            <div className={styles.subHeader}>
              {config.subHeader}
            </div>
            <div className={styles.pretext}>
              {config.pretext}
            </div>
            <div className={styles.time}>
              {config.timeText}
            </div>
            {config.onClick && (
              <Button
                className={styles.button}
                expand
                type="primary"
                size="large"
                onClick={config.onClick}
              >
                {config.buttonText}
              </Button>
            )}
          </div>
        }
        {isClaimed && propertyMap &&
          <SGT
            selectedUnits={selectedUnits}
            propertyMap={propertyMap}
            expiresAt={expiresAt}
          />
        }
        <TermsOfUseModal
          isOpen={isTermsModalOpen}
          closeModal={() => setIsTermsModalOpen(false)}
          loading={submitting}
          onAcceptTerms={async () => {
            setAcceptedTermsAt(moment.utc(new Date()).toISOString());
            setIsTermsModalOpen(false);
            await onClickActivateHandler();
          }}
        />
        <Footer />
      </>
    );
  }

  return null;
};

export default Visitor;
